import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Pencil, Eye, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api';
import { loadBillTemplates, seedBillTemplates, setBillTemplates } from '../../data/billTemplateStore';
import { effectiveBranchesOf } from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { ReceiptThumb } from '../../components/ReceiptThumb';
import { BillTemplateEditorPage } from './BillTemplateEditorPage';
import { PrintBillModal } from '../../components/PrintBillModal';
import { ConfirmDeleteDesign } from '../../components/ConfirmDeleteDesign';
import { SAMPLE_BILL } from '../../utils/receiptPreview';
import { fieldsOf, withShopText } from '../../data/billFields';
import {
  BILL_PRESETS,
  DEFAULT_SETTINGS,
  DEFAULT_TEMPLATE_ID,
  resolveTemplateSettings,
} from '../../data/billTemplates';

/**
 * Says which branches print with a design, on the design itself — so "what does
 * my shop actually print?" is answered here, without opening the Branches tab
 * where the assignment is made.
 */
const BranchUsage = ({ branches }) => {
  if (!branches.length) return null;
  const names = branches.map((b) => b.name).join(', ');
  return (
    <p className="text-[10px] font-semibold mt-0.5 truncate" style={{ color: 'var(--theme-primary)' }}>
      Printing at: {names}
    </p>
  );
};
BranchUsage.propTypes = { branches: PropTypes.array };

/**
 * One template in the gallery: a scaled-down rendering of the receipt itself,
 * its name, and what it is used for.
 *
 * A list of names told a shop nothing about what each design actually looked
 * like — they had to select one, read the preview, and select the next to
 * compare. Showing the paper is the whole point of the screen.
 */
const TemplateCard = ({ org, tpl, settings, branches, busy, onPreview, onPrimary, onDelete }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onPreview}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPreview(); } }}
    className="cursor-pointer rounded-lg border overflow-hidden flex flex-col transition-colors border-[var(--theme-tertiary)] hover:border-slate-300"
  >
    <div className="relative border-b border-slate-100">
      <ReceiptThumb org={org} settings={settings} />
    </div>

    <div className="p-2.5 flex flex-col gap-1 flex-1">
      <p className="text-[12px] font-bold text-[#0d0d0d] leading-tight truncate">{tpl.name}</p>
      <p className="text-[10px] text-slate-400">{settings.paperWidth} mm paper</p>
      <BranchUsage branches={branches} />

      <div className="flex items-center gap-1 mt-auto pt-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPrimary(); }}
          className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-md text-[10px] font-bold uppercase tracking-wider text-white disabled:opacity-50"
          style={{ background: 'var(--theme-primary)' }}
        >
          <Pencil size={11} /> Edit
        </button>
        {/* On the card, not in a panel below it. A single preview pane showed
            one design at a time and sat far from the card that fed it; here the
            bill is one press away from the design it belongs to. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPreview(); }}
          className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-[var(--theme-primary)] hover:bg-[var(--theme-secondary)]"
          aria-label={`Preview ${tpl.name}`}
          title="See this bill"
        >
          <Eye size={13} />
        </button>
        {/* Deleting asks first. It sits beside Edit, so a mis-tap is only ever
            one press from a question, never from a design gone — and the
            question names the branches whose bill would change. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          disabled={busy}
          className="h-7 w-7 grid place-items-center rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-50"
          aria-label={`Delete ${tpl.name}`}
          title="Delete this design"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  </div>
);
TemplateCard.propTypes = {
  org: PropTypes.object,
  tpl: PropTypes.object,
  settings: PropTypes.object,
  branches: PropTypes.array,
  busy: PropTypes.bool,
  onPreview: PropTypes.func,
  onPrimary: PropTypes.func,
  onDelete: PropTypes.func,
};

export const BillFormatTab = ({ org, showNotification }) => {
  const { version } = useSharedStore();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, name, settings }
  const [saving, setSaving] = useState(false);
  // The design whose bill is open, if any. It used to be a panel pinned below
  // the gallery showing one design at a time, far from the card that fed it.
  const [viewing, setViewing] = useState(null); // { name, cfg }
  const [deleting, setDeleting] = useState(null); // the design being confirmed

  const branches = useMemo(() => effectiveBranchesOf(org.id), [org.id, version]);

  /**
   * Everything the rest of this component reads, declared before any of it.
   *
   * `const` is not hoisted: reading one before its line has run throws, and
   * this component has an early `return` for the editor that reads `usedBy`.
   * With the declaration below that return the tab crashed outright the moment
   * a design was opened — "Cannot access 'usedBy' before initialization", and a
   * blank page. Keeping every helper above the first thing that uses it makes
   * that mistake impossible rather than merely absent.
   */

  // Which branches print with a given design, so a card can say so plainly and
  // the delete box can name them.
  const usedBy = (id) => branches.filter((b) => (b.billTemplateId || DEFAULT_TEMPLATE_ID) === id);

  // The starter designs this shop no longer has, matched by name — the same
  // way the server decides what to put back.
  const missing = BILL_PRESETS.filter(
    (p) => !templates.some((t) => t.name === p.name),
  );

  /**
   * Keep this tab's copy and the shared one in step after an edit.
   *
   * The till, the bill on screen and the Branches board all read the shared
   * copy. Updating only local state here meant a design saved in this tab did
   * not reach them until the page was reloaded — and the whole point of saving
   * one is that the next bill prints it.
   */
  const applyTemplates = (next) => {
    setTemplates(next);
    setBillTemplates(next);
  };

  useEffect(() => {
    let cancelled = false;
    // The starter designs are written on first open, so every design a shop
    // sees is a row it owns.
    //
    // The seed is attempted every time, and the server decides: it has the flag
    // saying whether it has run. Skipping it here when the list was non-empty
    // meant a shop that had already made one design of its own never received
    // the starter set at all — and since they had stopped being drawn from the
    // frontend, that shop was left looking at one design where there had been
    // nine. A failed call falls back to whatever loaded, so an older backend
    // without the route still shows the designs that exist.
    loadBillTemplates()
      .then((list) => seedBillTemplates().catch(() => list))
      .then((list) => { if (!cancelled) setTemplates(list); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Puts back a starter design the shop deleted. Matching is by name, so
  // anything they still have is left exactly as they last saved it.
  const restore = async () => {
    setSaving(true);
    try {
      applyTemplates(await seedBillTemplates({ force: true }));
      showNotification?.('Ready-made designs are back in your list.', 'success');
    } catch (e) {
      showNotification?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (tpl) => {
    const settings = { ...DEFAULT_SETTINGS, ...(tpl.settings || {}) };
    setEditing({
      id: tpl.id,
      name: tpl.name,
      // Designs saved before the builder existed have flags and no field list.
      // Converting on open means the shop sees the same receipt they had, as
      // fields they can now reorder — rather than an empty editor.
      settings: { ...settings, fields: withShopText(fieldsOf(settings), org) },
    });
  };

  // Ready-made designs are read-only, but pressing Edit on one should not make
  // a shop think about that: it takes a copy and opens it. The original stays
  // intact as something they can always fall back to.
  //
  // Nothing is written until Save. A design created on the server the moment
  // the editor opened left an abandoned row behind every time someone opened it
  // and changed their mind.
  const duplicate = (source) => {
    const settings = { ...DEFAULT_SETTINGS, ...(source.settings || {}) };
    setEditing({
      id: null,                       // null means "not saved yet"
      // Blank on purpose. A new design used to open already called "Standard
      // Receipt (my version)", a name nobody chose and everybody then had to
      // clear before typing their own.
      name: '',
      settings: { ...settings, fields: withShopText(fieldsOf(settings), org) },
    });
  };

  /**
   * Write the design being edited. A draft with no id is a new one and is
   * POSTed; an existing one is PUT. The editor does not need to know which.
   */
  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim()) {
      showNotification?.('Give the design a name first.', 'error');
      return;
    }
    setSaving(true);
    try {
      const isNew = !editing.id;
      const res = await fetchWithAuth(
        isNew ? '/bill-templates' : `/bill-templates/${editing.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          body: JSON.stringify({ name: editing.name.trim(), settings: editing.settings }),
        },
      );
      if (!res.ok) throw new Error('Could not save the design');
      const saved = await res.json();

      applyTemplates(isNew
        ? [...templates, saved]
        : templates.map((t) => (t.id === saved.id ? saved : t)));

      // Stay on the design just saved. Closing the editor used to drop the
      // preview back to the standard receipt, which read as "my changes were
      // lost" even though they had been written.
      setEditing(null);
      showNotification?.(`Saved "${saved.name}". Branches using it print this from the next bill.`, 'success');
    } catch (e) {
      showNotification?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tpl) => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(`/bill-templates/${tpl.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Could not delete the design');
      applyTemplates(templates.filter((t) => t.id !== tpl.id));
      if (editing?.id === tpl.id) setEditing(null);
      // Names the branches rather than saying "branches using it", so the shop
      // knows exactly whose bill just changed.
      const affected = usedBy(tpl.id).map((b) => b.name).join(', ');
      showNotification?.(
        affected
          ? `Deleted "${tpl.name}". ${affected} now prints Standard Receipt.`
          : `Deleted "${tpl.name}".`,
        'success',
      );
    } catch (e) {
      showNotification?.(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Editing replaces the tab, rather than sharing it with the gallery. The
  // same shape the superadmin app uses for creating an organization.
  if (editing) {
    return (
      <BillTemplateEditorPage
        org={org}
        draft={editing}
        setDraft={setEditing}
        onCancel={() => setEditing(null)}
        onSave={save}
        saving={saving}
        isNew={!editing.id}
      />
    );
  }



  return (
    <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-5">
      {/* One header, in the same shape as the Branches tab: what this screen is,
          how many things are on it, and the one action that adds another. The
          numbered "how this works" box it replaces explained a screen that now
          explains itself — every card carries its own Edit and preview. */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-[#0d0d0d]">Bill designs</h3>
          <p className="text-[11px] text-slate-500 mt-1 max-w-xl leading-relaxed">
            Press the eye to see a bill, <strong>Edit</strong> to change one. Give each branch
            its design in the <strong>Branches</strong> tab.
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {/* Only when there is something to put back. With every starter
              design present the button does nothing, and a control that does
              nothing is furniture. Shown, it is also information: something
              has been deleted, and this is how many. */}
          {missing.length > 0 && (
            <button
              type="button"
              onClick={restore}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-[var(--theme-tertiary)] bg-white text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:border-[var(--theme-primary)] hover:text-[var(--theme-primary)] transition-colors disabled:opacity-50"
              title="Puts back the ready-made designs you have deleted, leaving the ones you kept alone"
            >
              <RotateCcw size={13} /> Restore {missing.length} ready-made
            </button>
          )}
          <button
            type="button"
            onClick={() => duplicate(BILL_PRESETS[0])}
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: 'var(--theme-primary)' }}
          >
            <Plus size={13} /> New design
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-[11px] text-slate-400">Loading…</p>
      ) : (
        <section>
          {/* One group. There used to be two — "Ready-made" designs a shop
              could only copy, and its own — because the starter set lived in
              the frontend with no row behind it. They are rows now, so every
              design here is the shop's own: editable, renameable, removable. */}
          <h4 className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">
            {templates.length} {templates.length === 1 ? 'design' : 'designs'}
          </h4>

          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {templates.map((t) => (
              <TemplateCard
                key={t.id}
                org={org}
                tpl={t}
                settings={resolveTemplateSettings(t.id, templates)}
                branches={usedBy(t.id)}
                busy={saving}
                onPreview={() => setViewing({ name: t.name, cfg: resolveTemplateSettings(t.id, templates) })}
                onPrimary={() => startEdit(t)}
                onDelete={() => setDeleting(t)}
              />
            ))}

            <button
              type="button"
              onClick={() => duplicate(BILL_PRESETS[0])}
              disabled={saving}
              className="rounded-lg border-2 border-dashed border-[var(--theme-tertiary)] min-h-[170px] flex flex-col items-center justify-center gap-1.5 text-slate-400 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] transition-colors disabled:opacity-50"
            >
              <Plus size={18} />
              <span className="text-[10px] font-bold uppercase tracking-wider">New design</span>
            </button>
          </div>
        </section>
      )}

      {deleting && (
        <ConfirmDeleteDesign
          name={deleting.name}
          usedBy={usedBy(deleting.id)}
          busy={saving}
          onCancel={() => setDeleting(null)}
          onConfirm={() => { const t = deleting; setDeleting(null); remove(t); }}
        />
      )}

      {viewing && (
        <PrintBillModal
          org={org}
          bill={SAMPLE_BILL}
          settings={viewing.cfg}
          title={viewing.name}
          onClose={() => setViewing(null)}
        />
      )}
    </motion.div>
  );
};

BillFormatTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
